import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import * as Icons from "lucide-react";
import { images } from "../mock";

export const About = () => {
  const values = [
    {
      title: "Professionalität",
      description: "Langjährige Erfahrung im B2B-IT-Ankauf mit standardisierten Prozessen",
      icon: "Award",
    },
    {
      title: "Transparenz",
      description: "Klare Kommunikation und faire, nachvollziehbare Preisgestaltung",
      icon: "Eye",
    },
    {
      title: "Sicherheit",
      description: "DSGVO-konforme Datenlöschung und zertifizierte Verfahren",
      icon: "ShieldCheck",
    },
    {
      title: "Nachhaltigkeit",
      description: "Umweltgerechte Verwertung und Wiederaufbereitung von IT-Hardware",
      icon: "Leaf",
    },
  ];

  const stats = [
    { number: "10+", label: "Jahre Erfahrung" },
    { number: "500+", label: "Zufriedene Firmenkunden" },
    { number: "50.000+", label: "Angekaufte Geräte" },
    { number: "100%", label: "DSGVO-konform" },
  ];

  const team = [
    {
      role: "Geschäftsführung",
      description: "Langjährige IT-Branchenerfahrung und strategische Unternehmensführung",
      icon: "Users",
    },
    {
      role: "Ankauf & Bewertung",
      description: "Experten für faire Gerätebewertung und Preisgestaltung",
      icon: "Calculator",
    },
    {
      role: "Logistik",
      description: "Professionelle Abwicklung und deutschlandweite Koordination",
      icon: "Truck",
    },
    {
      role: "IT-Sicherheit",
      description: "Zertifizierte Spezialisten für Datenlöschung und IT-Security",
      icon: "Lock",
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Über uns
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              wirkaufendeinLaptop.de ist Ihr vertrauensvoller Partner für den professionellen 
              B2B-Ankauf von gebrauchten Firmenlaptops. Mit über 10 Jahren Erfahrung im 
              IT-Remarketing bieten wir Unternehmen eine sichere, transparente und 
              unkomplizierte Lösung für den Verkauf ihrer IT-Hardware.
            </p>
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <img
              src={images.modernWorkspace}
              alt="Modern Workspace"
              className="w-full h-96 object-cover rounded-2xl shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Card className="border-blue-200 shadow-lg">
              <CardContent className="p-12">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icons.Target className="text-white" size={32} />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Unsere Mission</h2>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed text-center">
                  Wir machen den Verkauf von gebrauchten Firmenlaptops so einfach, sicher 
                  und profitabel wie möglich. Dabei legen wir größten Wert auf Datensicherheit, 
                  Transparenz und nachhaltige IT-Verwertung. Unser Ziel ist es, Unternehmen 
                  einen maximalen Restwert ihrer IT-Hardware bei minimalem Aufwand zu ermöglichen.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Unsere Werte
            </h2>
            <p className="text-xl text-gray-600">
              Was uns antreibt und auszeichnet
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {values.map((value, index) => {
              const Icon = Icons[value.icon];
              return (
                <Card key={index} className="border-blue-100 hover:border-blue-300 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="text-blue-600" size={28} />
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                      {value.title}
                    </h3>
                    <p className="text-gray-600 text-lg">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Zahlen & Fakten
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl md:text-6xl font-bold mb-3">
                  {stat.number}
                </div>
                <div className="text-blue-100 text-lg">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Unser Team
            </h2>
            <p className="text-xl text-gray-600">
              Experten für professionellen IT-Ankauf
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {team.map((member, index) => {
              const Icon = Icons[member.icon];
              return (
                <Card key={index} className="border-blue-100 hover:border-blue-300 hover:shadow-md transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="text-blue-600" size={28} />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {member.role}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {member.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              Warum wirkaufendeinLaptop.de?
            </h2>
            <div className="space-y-6">
              {[
                {
                  title: "B2B-Spezialisierung",
                  description: "Ausschließlich auf Firmenkunden fokussiert – wir verstehen Ihre geschäftlichen Anforderungen",
                },
                {
                  title: "Professionelle Prozesse",
                  description: "Standardisierte und erprobte Abläufe für schnelle und sichere Abwicklung",
                },
                {
                  title: "Zertifizierte Datensicherheit",
                  description: "DSGVO-konforme Datenlöschung mit individuellen Zertifikaten für jedes Gerät",
                },
                {
                  title: "Faire Preise",
                  description: "Transparente Bewertung und attraktive Ankaufspreise basierend auf aktuellen Marktwerten",
                },
                {
                  title: "Deutschlandweiter Service",
                  description: "Kostenfreie Abholung an jedem Standort in Deutschland",
                },
                {
                  title: "Nachhaltige Verwertung",
                  description: "Umweltgerechte Aufbereitung und Wiedervermarktung für maximale Nachhaltigkeit",
                },
              ].map((reason, index) => (
                <Card key={index} className="border-blue-100 hover:shadow-md transition-all duration-300">
                  <CardContent className="p-6 flex items-start space-x-4">
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <Icons.Check className="text-white" size={20} />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {reason.title}
                      </h3>
                      <p className="text-gray-600">
                        {reason.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Überzeugt? Starten Sie jetzt!
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Werden Sie Teil unserer zufriedenen Firmenkunden
            </p>
            <Link to="/request-quote">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 font-medium shadow-lg">
                Jetzt Angebot anfragen
                <Icons.ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
